import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { ApiError } from "../../api/client";
import { Field, Input } from "../../components/ui/fields";
import { LogIn } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useDemoSignIn, useSessionState, useSignOut } from "../../api/hooks";
import { ErrorState, Loading } from "../../components/states";
import { Button, buttonClass } from "../../components/ui/button";
import { Card } from "../../components/ui/card";

/** Where /auth/login returns the browser: the page it was on. */
function loginHref(): string {
  const here = window.location.pathname + window.location.search;
  return `/auth/login?return_to=${encodeURIComponent(here)}`;
}

function DemoSignIn({ csrf }: { csrf: string | null }) {
  const { t } = useTranslation();
  const login = useDemoSignIn();
  const alert = useRef<HTMLParagraphElement>(null);
  const form = useForm<{ username: string; password: string }>({ defaultValues: { username: "", password: "" } });
  useEffect(() => { if (login.isError) alert.current?.focus(); }, [login.isError]);
  const submit = form.handleSubmit(({ username, password }) => { if (csrf) login.mutate({ username, password, csrf }); });
  const message = login.error instanceof ApiError && login.error.status === 429 ? t("account.demoRateLimited")
    : login.error instanceof ApiError && login.error.code === "csrf_failed" ? t("account.demoExpired")
    : login.error instanceof ApiError && login.error.code === "invalid_credentials" ? t("account.demoFailed") : t("account.demoUnavailable");
  return <section className="space-y-4 border-t border-border pt-5" aria-labelledby="demo-heading">
    <div className="space-y-1"><h2 id="demo-heading" className="font-semibold">{t("account.demoTitle")}</h2><p className="text-sm text-muted-foreground">{t("account.demoBody")}</p></div>
    <form onSubmit={(event) => void submit(event)} className="space-y-4">
      <Field label={t("account.demoUsername")} htmlFor="demo-username"><Input id="demo-username" className="h-11" autoComplete="username" autoCapitalize="none" spellCheck={false} required maxLength={64} disabled={login.isPending} {...form.register("username", { required: true })} /></Field>
      <Field label={t("account.demoPassword")} htmlFor="demo-password"><Input id="demo-password" className="h-11" type="password" autoComplete="current-password" required maxLength={256} disabled={login.isPending} {...form.register("password", { required: true })} /></Field>
      <p ref={alert} role={login.isError ? "alert" : undefined} aria-live="polite" tabIndex={-1} className="min-h-16 text-sm text-negative">{login.isError ? message : ""}</p>
      <Button type="submit" className="min-h-11 w-full" disabled={login.isPending || !csrf}>{login.isPending ? t("account.demoSigningIn") : t("account.demoSignIn")}</Button>
    </form>
  </section>;
}

/** Shows its children only to a signed-in person who may use the site. */
export function SessionGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const session = useSessionState();
  const signOut = useSignOut();
  if (session.isPending) return <Loading />;
  if (session.isError) return <ErrorState title={t("account.unavailable")} error={session.error} onRetry={() => void session.refetch()} />;
  const state = session.data;
  if (state.user) return <>{state.user.roles.includes("demo") ? <p role="note" className="border-b border-border bg-muted px-4 py-2 text-sm text-muted-foreground">{t("account.demoNotice")}</p> : null}{children}</>;
  const failed = new URLSearchParams(window.location.search).get("login") === "failed";
  return (
    <div className="mx-auto my-10 w-full max-w-md px-4 sm:my-16">
      <Card className="space-y-4 p-6">
        {state.denied ? (
          <>
            <h2 className="text-lg font-semibold">{t("account.denied")}</h2>
            <p className="text-sm text-muted-foreground">{state.denied}</p>
            <Button variant="outline" onClick={() => signOut.mutate()} disabled={signOut.isPending}>
              {t("account.signOut")}
            </Button>
          </>
        ) : (
          <>
            <h1 className="text-lg font-semibold">{t("account.signInTitle")}</h1>
            <p className="text-sm text-muted-foreground">{t("account.signInBody")}</p>
            {failed ? (
              <p role="alert" className="text-sm text-negative">
                {t("account.loginFailed")}
              </p>
            ) : null}
            {state.login.mode === "none" ? (
              state.login.demo?.enabled ? null : <p className="text-sm text-negative">{t("account.unavailable")}</p>
            ) : (
              <a href={loginHref()} className={`${buttonClass()} min-h-11 w-full justify-center`}>
                <LogIn className="h-4 w-4" aria-hidden />
                {t("account.signIn")}
              </a>
            )}
            {state.login.demo?.enabled ? <DemoSignIn csrf={state.login.demo.csrfToken} /> : null}
            {state.login.mode === "development" ? <p className="text-xs text-muted-foreground">{t("account.developmentNote")}</p> : null}
          </>
        )}
      </Card>
    </div>
  );
}

export function AccountControl() {
  const { t } = useTranslation();
  const session = useSessionState();
  const signOut = useSignOut();
  const user = session.data?.user;
  if (!user) return null;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="hidden text-muted-foreground sm:inline" title={user.email}>
        {user.name ?? user.email}
      </span>
      <Button variant="outline" size="sm" onClick={() => signOut.mutate()} disabled={signOut.isPending}>
        {t("account.signOut")}
      </Button>
    </div>
  );
}
